/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { consoleLog } from 'utils/utility'
import { toast } from 'utils/toastUtils'
import clsx from 'clsx'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import T from 'components/T'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useAppDispatch } from 'helpers/store'
import ReservationsAction from 'store/reservations/ReservationsAction'
import { Reservation } from 'types/entities/Reservation'
import UiTextarea from 'components/Ui/UiTextarea'
import { UiButtonType } from 'components/Ui/UiButton'
import {
    BookingSellerInvoiceNoteInputs,
    useBookingSellerInvoiceNoteSchema,
} from 'pages/Front/Booking/Seller/schemas/useBookingSellerInvoiceNoteSchema'

// typing provided by schema file

interface ReservationInvoiceNoteProps {
    reservation: Reservation
    className?: string
}

const ReservationInvoiceNote = (props: ReservationInvoiceNoteProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const BookingSellerInvoiceNoteSchema = useBookingSellerInvoiceNoteSchema()
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<BookingSellerInvoiceNoteInputs>({
        resolver: zodResolver(BookingSellerInvoiceNoteSchema),
        defaultValues: {
            invoice_note:
                'extra' in props.reservation && props.reservation?.extra?.invoice_note
                    ? props.reservation.extra.invoice_note
                    : null,
        },
    })

    const onErrorHandler = (error: unknown) => {
        consoleLog('onErrorHandler', error)
    }

    const onSubmitHandler = (data: BookingSellerInvoiceNoteInputs) => {
        if (isLoading) {
            return
        }
        setIsLoading(true)

        dispatch(ReservationsAction.fetchSetInvoiceNoteToReservation(props.reservation.id, data.invoice_note || ''))
            .then(() => {
                toast.success(t('Invoice note saved'))
            })
            .catch((error) => {
                consoleLog('error', error)
                toast.error(t('Invoice note not saved'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <div className={clsx('ReservationInvoiceNote UiCard p-2 w-full h-full flex flex-col', props.className)}>
            <form
                className={clsx('flex flex-col w-full h-full')}
                onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}
            >
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700 shrink-0">
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Invoice note')}</h3>
                    <button
                        type={UiButtonType.Submit}
                        className="p-1 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 rounded transition-colors"
                        title={t('Save') as string}
                    >
                        <CheckIcon className="w-4 h-4 text-white" />
                    </button>
                </div>

                <div className={clsx('flex-1 flex flex-col overflow-hidden')}>
                    <UiTextarea
                        rows={3}
                        {...register('invoice_note')}
                        error={errors?.invoice_note?.message}
                        className={clsx('w-full flex-1 text-xs')}
                        onBlur={handleSubmit(onSubmitHandler, onErrorHandler)}
                    />

                    {errors?.['invoice_note']?.message && (
                        <div className={clsx('text-red-500 mt-1 text-xs flex flex-row items-center shrink-0')}>
                            <div className={clsx('flex flex-row items-center')}>
                                <ExclamationCircleIcon
                                    className={'min-w-[14px] min-h-[14px] w-[14px] h-[14px] mr-0.5'}
                                />
                                <T>{errors?.['invoice_note']?.message}</T>
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </div>
    )
}

export default ReservationInvoiceNote
